
import { mixins } from "vue-class-component";
import { DateUtilsMixin } from "@/mixins/date-utils-mixin";
import { TransplantMixin } from "@/mixins/transplant-mixin";
import { Getter, State } from "vuex-class";
import { Component, Vue, Prop } from "vue-property-decorator";
import { IdLookup } from '@/store/validations/types';
import SubSection from "@/components/shared/SubSection.vue";
import { LungTransplantAttributes, RecipientJourney, TransplantFactors } from "@/store/recipientJourney/types";
import DateInput from "@/components/shared/DateInput.vue";
import { RecipientTransplantAttributes } from "@/store/recipientJourney/types";
import TimeInput from "@/components/shared/TimeInput.vue";
import TextInput from '@/components/shared/TextInput.vue';
import NumberInput from '@/components/shared/NumberInput.vue';
import { SaveableSection, SaveProvider, SaveResult } from '@/types';
import { Recipient } from '@/store/recipients/types';
import { Organ, OrganSpecification } from '@/store/lookups/types';
import SelectInput from '@/components/shared/SelectInput.vue';
import { TransplantSectionPageState } from '@/mixins/transplant-mixin';

export interface LungTransplantDetailsPageState {
  transplantType?:number;
  transplantStartDate?: string;
  transplantStartTime?: string;
  removedFromColdDateLeft?: string;
  removedFromColdTimeLeft?: string;
  removedFromColdDateRight?: string;
  removedFromColdTimeRight?: string;
  clampRemovalDateLeft?: string;
  clampRemovalTimeLeft?: string;
  clampRemovalDateRight?: string;
  clampRemovalTimeRight?: string;
  rewarmTimeLeft?: string;
  rewarmTimeRight?: string;
  coldIschTimeLeft?: string;
  coldIschTimeRight?: string;
  totalIschtimeLeft?: string;
  totalIschtimeRight?: string;
}

@Component({
  components: {
    SubSection,
    DateInput,
    TimeInput,
    SelectInput,
    TextInput
  }
})
export default class LungTransplantDetails extends mixins(DateUtilsMixin, TransplantMixin) implements SaveableSection{
  // State
  @State(state => state.journeyState.selectedJourney) journey!: RecipientJourney;
  @State(state => state.recipients.selectedRecipient) recipient!: Recipient;

  // Getters
  @Getter('clientId', { namespace: 'recipients' }) private recipientId!: string;
  @Getter("transplantTypeLookup", { namespace: "lookups" }) organSpecification!: (organCode: number | string | undefined) => OrganSpecification[] | undefined;
  @Getter('canSaveGetter', { namespace: 'validations' }) private canSaveGetter!: (newRecord: boolean) => boolean;
  @Getter('convertTimeInSeconds', { namespace: 'utilities' }) convertTimeInSeconds!: (time?: number) => string;
  @Getter('isGroupWriteable', { namespace: 'validations' }) private isGroupWriteable!: (groupName: string) => boolean;

  // Props
  @Prop({ default: false }) newJourney!: boolean;
  @Prop({ default: false }) canSave!: boolean;

  private initialTransplantType: number|undefined = undefined;

  private loaded(): void {
    this.$emit('loaded', 'lung-transplant-details');
  }

  /**
   * Return true if we can edit the transplant details
   *
   * @returns {boolean} true if we can edit
   */
  get canEdit(): boolean{
    if (this.newJourney || this.journey.completed) {
      return false;
    }
    return true;
  }

  /**
   * Returns an array of options for Transplant Type
   * 
   * 
   * @returns {OrganSpecification[]|[]} organ-specific options 
   */
  get transplantTypeLookup(): OrganSpecification[] {
    const organCode = this.journey?.organ_code;
    if (!organCode) {
      return [];
    }
    // Fetch appropriate options
    const options = this.organSpecification(organCode) || [];

    const translated = options.map((option: OrganSpecification): OrganSpecification => {
      return {
        ...option,
        value: this.$t(option.value).toString(),
      };
    });
    return translated;
  }

  /**
   * Build state for the LungTransplantDetails form area
   *
   * @param LungTransplantAttributes from RecipientTransplantAttributes
   * @returns {LungTransplantDetailsPageState} form state for HeartTransplant
   */
  public buildTransplantDetailsState(transplantDetails?: RecipientTransplantAttributes): LungTransplantDetailsPageState {
    const lungTransplantAttributes = transplantDetails as LungTransplantAttributes || {};
    this.initialTransplantType = lungTransplantAttributes.organ_specification_code || undefined;
    const result: LungTransplantDetailsPageState = {
      transplantType: lungTransplantAttributes.organ_specification_code || undefined,
      transplantStartDate: this.parseDateUiFromDateTime(lungTransplantAttributes.transplant_start_date || undefined),
      transplantStartTime: this.parseTimeUiFromDateTime(lungTransplantAttributes.transplant_start_date || undefined),
      removedFromColdDateLeft: this.parseDateUiFromDateTime(lungTransplantAttributes.end_cold_storage_date_left || undefined),
      removedFromColdTimeLeft: this.parseTimeUiFromDateTime(lungTransplantAttributes.end_cold_storage_date_left || undefined),
      removedFromColdDateRight: this.parseDateUiFromDateTime(lungTransplantAttributes.end_cold_storage_date_right || undefined),
      removedFromColdTimeRight: this.parseTimeUiFromDateTime(lungTransplantAttributes.end_cold_storage_date_right || undefined),
      clampRemovalDateLeft: this.parseDateUiFromDateTime(lungTransplantAttributes.clamp_removal_date_left || undefined),
      clampRemovalTimeLeft: this.parseTimeUiFromDateTime(lungTransplantAttributes.clamp_removal_date_left || undefined),
      clampRemovalDateRight: this.parseDateUiFromDateTime(lungTransplantAttributes.clamp_removal_date_right || undefined),
      clampRemovalTimeRight: this.parseTimeUiFromDateTime(lungTransplantAttributes.clamp_removal_date_right || undefined),
      rewarmTimeLeft: this.convertTimeInSeconds(lungTransplantAttributes.rewarm_time_left || 0), 
      rewarmTimeRight: this.convertTimeInSeconds(lungTransplantAttributes.rewarm_time_right || 0), 
      coldIschTimeRight: this.convertTimeInSeconds(lungTransplantAttributes.cold_isch_time_right || 0),
      coldIschTimeLeft: this.convertTimeInSeconds(lungTransplantAttributes.cold_isch_time_left || 0),
      totalIschtimeLeft: this.convertTimeInSeconds(lungTransplantAttributes.total_isch_time_left || 0),
      totalIschtimeRight: this.convertTimeInSeconds(lungTransplantAttributes.total_isch_time_right || 0),
    };
    return result;
  }

  /**
   * Gets a patch object representing form edit state changes for this form
   *
   * Delegates the logic of building the patch to a local private method
   *
   * @returns {any} patch object containing field changes
   */
  public extractPatch(): any {
    if (!this.editState || !this.editState.transplantDetails || !this.editState.transplantDetails.organTransplantDetails) {
      return {};
    } else {
      return this.extractLungTransplantDetailsPatch(this.editState.transplantDetails.organTransplantDetails);
    }
  }

  // String for confirmation dialog for partial cluster transplant hold
  get confirmationText(): string {
    if (!this.isConfirmationRequired()) return '';

    return this.$t('confirm_partial_cluster_transplant_hold').toString();
  }

  /**
   * Saves the form edit state.
   *
   * Prepares a payload for Transplant Atributes, dispatches a save action, and registers the save result.
   * @emits clear clear validation errors because saving has begun
   * @emits saved saving has completed successful
   */
  public savePatch(): void {
    // if transplantType has changed after building the state then alert user for transplant type change
    // except on initial save when initialTransplantType is still undefined
    if (this.initialTransplantType !== undefined && this.initialTransplantType !== this.editState.transplantDetails?.organTransplantDetails?.transplantType) {
      alert(this.$t('alert_transplant_type_change').toString());
    }
    // Refer to the save provider that handles this form area
    const saveProvider = this.$refs.saveTransplantDetails as unknown as SaveProvider;
    // Report to parent that saving has began
    this.$emit('clear');
    // Generate payload based on current edit state
    const transplantAttributesPatch = this.extractPatch();
    // Setup saving payload
    const payload = {
      journeyId: this.journey._id ? this.journey._id : undefined,
      recipientId: this.recipientId,
      transplantAttributes: transplantAttributesPatch,
    };
    // Dispatch save action and register the response
    this.$store.dispatch('journeyState/saveTransplantDetails', payload).then((success: SaveResult) => {
      this.$emit('saved', 'lung-transplant-details');
      saveProvider.registerSaveResult(success);
    }).catch((error: SaveResult) => {
      // Emit event to handle errors
      this.$emit('handleErrors', error);
      // Show error notification
      saveProvider.registerSaveResult(error);
    });
  }

   /**
   * Clears all save notifications shown by the form.
   *
   * Gets the Save Provider associated with the form, and requests that it reset its own Save Toolbar
   */
  public resetSaveToolbar(): void {
    const saveProvider = this.$refs.saveTransplantDetails as unknown as SaveProvider;
    saveProvider.resetSaveToolbar();
  }


  // API response keys on the left, id for our UI on the right
  public idLookup(): IdLookup {
    return {
      'organ_specification_code' : 'transplantType',
      'transplant_start_date' : 'transplantStartDate',
      'end_cold_storage_date_left' : 'removedFromColdDateLeft',
      'end_cold_storage_date_right' : 'removedFromColdDateRight',
      'clamp_removal_date_left'  : 'clampRemovalDateLeft',
      'clamp_removal_date_right'  : 'clampRemovalDateRight',
      'transplant_start_time' : 'transplantStartTime',
      'end_cold_storage_time_left' : 'removedFromColdTimeLeft',
      'end_cold_storage_time_right' : 'removedFromColdTimeRight',
      'clamp_removal_time_left'  : 'clampRemovalTimeLeft',
      'clamp_removal_time_right'  : 'clampRemovalTimeRight',
    };

  }

  /**
   * Returns a journey patch object containing changes from a Transplant Details form
   *
   * @returns {RecipientTransplantAttributes}
   */
  private extractLungTransplantDetailsPatch(transplantDetails: LungTransplantDetailsPageState): LungTransplantAttributes {
    const transplant = this.editState.transplantDetails || {};
    const lungTransplantDetails = transplant.organTransplantDetails as LungTransplantDetailsPageState || {};

    // Fetch Transplant Date from the top-level of the form
    const transplantDate = this.editState.transplantDetails?.transplantDate;

    // Build nested 'factors' object to include in the Transplant Attributes patch
    const factors: TransplantFactors = {
      transplant_date: transplantDate ? this.sanitizeDateApi(transplantDate) : null,
    };

    // Build patch for Transplant Attributes object
    const result: LungTransplantAttributes = {
      organ_specification_code: lungTransplantDetails.transplantType,
      transplant_start_date: this.sanitizeDateTimeApi(lungTransplantDetails.transplantStartDate, lungTransplantDetails.transplantStartTime),
      end_cold_storage_date_left: this.sanitizeDateTimeApi(lungTransplantDetails.removedFromColdDateLeft, lungTransplantDetails.removedFromColdTimeLeft),
      end_cold_storage_date_right: this.sanitizeDateTimeApi(lungTransplantDetails.removedFromColdDateRight, lungTransplantDetails.removedFromColdTimeRight),
      clamp_removal_date_left: this.sanitizeDateTimeApi(lungTransplantDetails.clampRemovalDateLeft, lungTransplantDetails.clampRemovalTimeLeft),
      clamp_removal_date_right: this.sanitizeDateTimeApi(lungTransplantDetails.clampRemovalDateRight, lungTransplantDetails.clampRemovalTimeRight),
      factors,
      donor_id: transplant.donorId ? transplant.donorId : null,
    };

    // Copy Details to Clustered Organs for details see https://shore.tpondemand.com/entity/7541-722-v42-transplant-details
    if (this.isClustered) {
      result.copy_to_cluster = transplant.copyToCluster;
    }

    return result;
  }
}
